import { useEffect, useRef, useState } from "react";
import { CalendarPlus, ChevronDown } from "lucide-react";
import { ShareButton } from "./ShareButton";
import { CARNIVAL_UI } from "../lib/carnivalMeta";
import {
  buildEventIcs,
  downloadIcs,
  buildGoogleCalendarUrl,
} from "../lib/carnivalIcs";

const SITE = "https://islandsdaily.com";

/**
 * Per-event action strip: [Share] [Add to Calendar]
 *
 * Both actions are optional-and-tiny — they must not crowd the event
 * row's date+label+description hierarchy. On mobile they collapse
 * onto a second line under the description.
 *
 * WhatsApp share URL includes `?ref=cshare` for attribution the same
 * way the digest forwards use `?ref=fw` (see lib/referral.js).
 */
export const CarnivalEventActions = ({ event, islandKey, lang }) => {
  const [calOpen, setCalOpen] = useState(false);
  const calRef = useRef(null);
  const label = event.labels?.[lang] || event.labels?.en || event.key;
  const about = event.about?.[lang] || event.about?.en || "";

  const shareUrl = `${SITE}/${islandKey}/carnival?ref=cshare#${event.key}`;
  const shareText = `${label}${event.date ? ` — ${event.date}` : ""}`;

  useEffect(() => {
    if (!calOpen) return;
    const onDown = (e) => {
      if (calRef.current && !calRef.current.contains(e.target)) setCalOpen(false);
    };
    const onKey = (e) => { if (e.key === "Escape") setCalOpen(false); };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [calOpen]);

  const estimatedNote =
    (CARNIVAL_UI.calendar_estimated_note && CARNIVAL_UI.calendar_estimated_note[lang]) ||
    CARNIVAL_UI.calendar_estimated_note.en;

  const onDownloadIcs = (e) => {
    e.stopPropagation();
    e.preventDefault();
    const ics = buildEventIcs({
      islandKey,
      event,
      label,
      description: about,
      siteOrigin: SITE,
      estimatedNote,
    });
    if (ics) {
      downloadIcs(ics, `${islandKey}-${event.key}-${event.date}`);
    }
    setCalOpen(false);
  };

  const gcalUrl = buildGoogleCalendarUrl({
    event,
    label,
    description: about,
    islandKey,
    siteOrigin: SITE,
    estimatedNote,
  });

  const noDate = !event.date;

  return (
    <div className="flex flex-wrap items-center gap-2 mt-2.5">
      <ShareButton
        title={label}
        text={shareText}
        url={shareUrl}
        testId={`carnival-share-${event.key}`}
        compact
      />
      {!noDate && (
        <div className="relative" ref={calRef}>
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); setCalOpen((v) => !v); }}
            className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-[11.5px] text-[#3D4C52] border border-[#E5E7EB] hover:border-[#0AADA0] hover:text-[#0AADA0] transition-colors bg-white"
            data-testid={`carnival-calendar-trigger-${event.key}`}
            aria-label={CARNIVAL_UI.add_to_calendar[lang] || CARNIVAL_UI.add_to_calendar.en}
            aria-expanded={calOpen}
            aria-haspopup="true"
          >
            <CalendarPlus size={12} />
            {CARNIVAL_UI.add_to_calendar[lang] || CARNIVAL_UI.add_to_calendar.en}
            <ChevronDown size={11} />
          </button>
          {calOpen && (
            <div
              className="absolute z-20 left-0 mt-1 w-72 bg-white border border-[#E5E7EB] rounded-lg shadow-lg p-2 space-y-1"
              role="menu"
              data-testid={`carnival-calendar-menu-${event.key}`}
            >
              <a
                href={gcalUrl}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => { e.stopPropagation(); setCalOpen(false); }}
                className="block px-3 py-2 rounded text-[13px] text-[#1A2E35] hover:bg-[#F5F5F5]"
                role="menuitem"
                data-testid={`carnival-calendar-google-${event.key}`}
              >
                {CARNIVAL_UI.add_to_calendar_google[lang] || CARNIVAL_UI.add_to_calendar_google.en}
              </a>
              <button
                type="button"
                onClick={onDownloadIcs}
                className="block w-full text-left px-3 py-2 rounded text-[13px] text-[#1A2E35] hover:bg-[#F5F5F5]"
                role="menuitem"
                data-testid={`carnival-calendar-ics-${event.key}`}
              >
                {CARNIVAL_UI.add_to_calendar_ics[lang] || CARNIVAL_UI.add_to_calendar_ics.en}
              </button>
              {event.status === "estimated" && (
                <p className="text-[10.5px] text-[#6B7280] px-3 pt-1 pb-0.5 border-t border-[#F5F5F5]">
                  {estimatedNote}
                </p>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};
